import type { FundTransactionStatus } from '@/types/fund-transactions';

// Contact role options
export const CONTACT_ROLE_OPTIONS: Record<string, string> = {
  money_manager: 'Money Manager',
  authorized_signatory: 'Authorized Signatory',
  beneficial_owner: 'Beneficial Owner',
  contact_person: 'Contact Person',
  legal_counsel: 'Legal Counsel',
} as const;

// Portal access roles for investor contacts
// Note: null/undefined means no portal access (revoked)
export type InvestorPortalRole =
  | 'full_access'
  | 'investment_officer'
  | 'family_office_manager'
  | 'operational_support'
  | 'accountant';

// Portal modules that can be accessed
export type PortalModule =
  | 'dashboard'
  | 'holdings'
  | 'transactions'
  | 'documents'
  | 'profile'
  | 'fund_carry'
  | 'change_data';

export interface InvestorContact {
  id: string;
  investor_id: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  mobile?: string;
  role: string;
  title?: string;
  department?: string;
  is_primary: boolean;
  is_authorized_signatory: boolean;
  receives_reports: boolean;
  receives_capital_calls: boolean;
  receives_distributions: boolean;
  portal_role_id: string | null; // FK to investor_portal_roles table
  portal_role?: InvestorPortalRole | null; // Role code from backend (read-only, for display)
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  postcode?: string;
  country?: string;
  created_at: string;
  updated_at: string;
}

export interface ApiInvestorFundRelationship extends Omit<
  InvestorFundRelationship,
  | 'commitment_amount'
  | 'total_called'
  | 'total_contributed'
  | 'total_distributed'
> {
  commitment_amount: string;
  total_called: string;
  total_contributed: string;
  total_distributed: string;
}

export interface InvestorFundRelationship {
  id: string;
  investor_id: string;
  fund_id: string;
  commitment_amount: number;
  commitment_currency: string;
  contribution_percentage?: number;
  status: 'ACTIVE' | 'EXITED' | 'DEFAULTED' | 'PENDING';
  commitment_date?: string;
  first_closing_date?: string;
  exit_date?: string;

  // Custom LPA Terms
  has_custom_terms: boolean;

  // Custom Management Fees - Investment Period
  custom_investment_period_fee_rate?: number;
  custom_investment_period_fee_basis_id?: number;

  // Custom Management Fees - Post Investment Period
  custom_post_investment_period_fee_rate?: number;
  custom_post_investment_period_fee_basis_id?: number;

  // Custom Investment Period & Terms
  custom_investment_period_duration?: number;
  custom_gp_commitment?: number;

  // Custom Waterfall Structure
  custom_hurdle_rate?: number;
  custom_carried_interest?: number;
  custom_catch_up_rate?: number;
  custom_distribution_waterfall_type_id?: number;

  // Populated relationships for display
  custom_investment_period_fee_basis?: {
    id: number;
    code: string;
    name: string;
  };
  custom_post_investment_period_fee_basis?: {
    id: number;
    code: string;
    name: string;
  };
  custom_distribution_waterfall_type?: {
    id: number;
    code: string;
    name: string;
  };

  subscription_agreement_executed: boolean;
  total_called: number;
  total_contributed: number;
  total_distributed: number;
  created_at: string;
  updated_at: string;
}

interface InvestorBase {
  name: string;
  investor_type:
    | 'corporate'
    | 'family_office'
    | 'individual'
    | 'fund_of_funds'
    | 'institutional';
  is_individual: boolean;
  investing_fund_id?: string | null;

  // Entity-specific fields
  legal_form?: string;
  business_nature?: string;
  registration_number?: string;
  directors?: string;
  incorporation_date?: string;

  // Individual-specific fields
  date_of_birth?: string;
  identification_document?: string;
  id_document_number?: string;

  // Address Information
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  postcode?: string;
  country?: string;
  correspondence_address?: string;

  // Tax Information
  tax_domicile_country?: string;
  taxpayer_reference_number?: string;

  // Banking Information
  bank_account_name?: string;
  bank_name?: string;
  bank_country?: string;
  bank_address?: string;
  bank_swift_code?: string;
  bank_currency?: string;
  iban?: string;
  /** Account number for non-IBAN banks (e.g. US); set when there is no IBAN. */
  bank_account_number?: string;

  // Contact Information
  telephone_number?: string;

  // Compliance and Risk Assessment
  beneficial_owners?: string;
  is_direct_investor: boolean;
  is_high_risk_country: boolean;
  is_high_risk_sector: boolean;
  has_complex_legal_structure: boolean;
  connection_intermediary?: string;

  // KYC/AML Status
  kyc_status: 'pending' | 'verified' | 'rejected';
  aml_status: 'pending' | 'verified' | 'rejected';

  // System fields
  is_onboarded: boolean;
  import_identifier?: string;
}

export interface ApiInvestorFundSummary extends Omit<
  InvestorFundSummary,
  'commitment_amount'
> {
  commitment_amount: string;
}

export interface InvestorFundSummary {
  fund_id: string;
  fund_name: string;
  commitment_amount: number;
  commitment_currency: string;
}

export interface InvestorContactSummary {
  id: string;
  name: string;
  last_portal_activity?: string;
}

/**
 * An existing contact offered for copying onto another investor. Carries the
 * person's own details and notification preferences. Authority over one
 * specific investor - primary flag, signatory rights, portal access - is
 * absent by design and must be granted again on the new investor.
 */
export interface InvestorContactSearchResult {
  id: string;
  first_name: string;
  last_name: string;
  email?: string | null;
  phone?: string | null;
  mobile?: string | null;
  role: string;
  title?: string | null;
  department?: string | null;
  receives_reports: boolean;
  receives_capital_calls: boolean;
  receives_distributions: boolean;
  address_line_1?: string | null;
  address_line_2?: string | null;
  city?: string | null;
  postcode?: string | null;
  country?: string | null;
  /** Investor the contact currently belongs to - shown to disambiguate names. */
  investor_id: string;
  investor_name: string;
}

/** Whether a picker keeps the stand-in rows funds get when they invest. */
export type FundProxyVisibility = 'include' | 'exclude';

export interface InvestorSummary {
  kyc_status: 'pending' | 'verified' | 'rejected';
  country: string;
  id: string;
  name: string;
  investor_type:
    | 'corporate'
    | 'family_office'
    | 'individual'
    | 'fund_of_funds'
    | 'institutional';
  is_individual: boolean;
  /** Set when this investor is a proxy for another fund we administer. */
  investing_fund_id?: string | null;
  is_archived?: boolean;
  contacts: InvestorContactSummary[];
  fund_relationships: InvestorFundSummary[];
  pending_change_request_count: number;
}

export interface PaginatedInvestorSummary {
  items: InvestorSummary[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface InvestorDetail extends InvestorBase {
  id: string;
  is_archived?: boolean;
  /** Registry bank matched from the IBAN; null/undefined when off-list. */
  bank_code_id?: string | null;
  kyc_completion_date?: string;
  aml_completion_date?: string;
  created_at: string;
  updated_at: string;
  contacts: InvestorContact[];
  fund_relationships: InvestorFundRelationship[];
}

// Minimal response types for PUT operations (write/read separation)
export interface InvestorUpdateResponse {
  id: string;
  name: string;
  investor_type: string;
  kyc_status: string;
  updated_at: string;
}

export interface InvestorContactUpdateResponse {
  id: string;
  investor_id: string;
  updated_at: string;
}

export interface InvestorFundRelationshipUpdateResponse {
  id: string;
  investor_id: string;
  fund_id: string;
  status: string;
  updated_at: string;
}

export interface InvestorFundTransferRequest {
  fund_id: string;
  transferor_investor_id: string;
  transferor_relationship_id: string;
  transaction_name?: string;
  effective_date: string;
  transfer_commitment_currency: string;
  include_equalization: boolean;
  transferee_allocations: InvestorFundTransferAllocationRequest[];
}

interface InvestorFundTransferAllocationRequest {
  transferee_investor_id: string;
  transfer_commitment_amount: number;
}

export interface ApiInvestorFundTransferAllocationResponse extends Omit<
  InvestorFundTransferAllocationResponse,
  | 'transferred_commitment_amount'
  | 'transferred_called_amount'
  | 'transferred_contributed_amount'
  | 'transferred_distributed_amount'
  | 'transferred_unfunded_commitment_amount'
  | 'transferred_cost_basis_amount'
> {
  transferred_commitment_amount: string;
  transferred_called_amount: string;
  transferred_contributed_amount: string;
  transferred_distributed_amount: string;
  transferred_unfunded_commitment_amount: string;
  transferred_cost_basis_amount: string;
}

export interface InvestorFundTransferAllocationResponse {
  transferee_investor_id: string;
  transferee_relationship_id: string;
  transferred_commitment_amount: number;
  transferred_called_amount: number;
  transferred_contributed_amount: number;
  transferred_distributed_amount: number;
  transferred_unfunded_commitment_amount: number;
  transferred_cost_basis_amount: number;
  fund_transaction_id: string;
}

export interface ApiInvestorFundTransferResponse extends Omit<
  InvestorFundTransferResponse,
  | 'transferred_commitment_amount'
  | 'transferred_called_amount'
  | 'transferred_contributed_amount'
  | 'transferred_distributed_amount'
  | 'transferred_unfunded_commitment_amount'
  | 'transferred_cost_basis_amount'
  | 'allocations'
> {
  transferred_commitment_amount: string;
  transferred_called_amount: string;
  transferred_contributed_amount: string;
  transferred_distributed_amount: string;
  transferred_unfunded_commitment_amount: string;
  transferred_cost_basis_amount: string;
  allocations: ApiInvestorFundTransferAllocationResponse[];
}

export interface InvestorFundTransferResponse {
  fund_id: string;
  transferor_investor_id: string;
  transferor_relationship_id: string;
  transferee_investor_id?: string | null;
  transferee_relationship_id?: string | null;
  transferred_commitment_amount: number;
  transferred_called_amount: number;
  transferred_contributed_amount: number;
  transferred_distributed_amount: number;
  transferred_unfunded_commitment_amount: number;
  transferred_cost_basis_amount: number;
  effective_date: string;
  transaction_name?: string | null;
  fund_transaction_id?: string | null;
  include_equalization: boolean;
  allocations: InvestorFundTransferAllocationResponse[];
}

export interface InvestorWithFundContext extends InvestorSummary {
  fund_relationship?: InvestorFundRelationship;
}

export interface InvestorStatistics {
  total_investors: number;
  corporate_count: number;
  family_office_count: number;
  individual_count: number;
  fund_of_funds_count: number;
  verified_kyc_count: number;
  pending_kyc_count: number;
  onboarded_count: number;
}

export interface ApiFundInvestorStatistics extends Omit<
  FundInvestorStatistics,
  'total_commitments' | 'total_contributed' | 'total_distributed'
> {
  total_commitments: string;
  total_contributed: string;
  total_distributed: string;
}

export interface FundInvestorStatistics {
  total_investors: number;
  total_commitments: number;
  total_contributed: number;
  total_distributed: number;
  commitment_currency: string;
  active_investors: number;
  exited_investors: number;
}

// Form types
export interface InvestorCreateData extends Omit<
  InvestorBase,
  'kyc_status' | 'aml_status' | 'is_onboarded'
> {
  kyc_status?: 'pending' | 'verified' | 'rejected';
  aml_status?: 'pending' | 'verified' | 'rejected';
  is_onboarded?: boolean;
  contacts?: InvestorContactCreateData[];
}

export type InvestorContactCreateData = Omit<
  InvestorContact,
  'id' | 'investor_id' | 'created_at' | 'updated_at'
>;

export type DraftInvestorContact = InvestorContactCreateData & {
  _uiKey: string;
};

export type InvestorContactUpdateData = Partial<InvestorContactCreateData>;

export type InvestorUpdateData = Partial<InvestorCreateData>;

interface InvestorFundRelationshipCreateData extends Omit<
  InvestorFundRelationship,
  | 'id'
  | 'total_called'
  | 'total_contributed'
  | 'total_distributed'
  | 'created_at'
  | 'updated_at'
> {
  investor_id: string;
}

export type InvestorFundRelationshipUpdateData = Partial<
  Omit<InvestorFundRelationshipCreateData, 'investor_id' | 'fund_id'>
>;

interface InvestorFundRelationshipBulkItem {
  investor_id: string;
  commitment_amount: number;
}

export interface InvestorFundRelationshipBulkCreateData {
  fund_id: string;
  commitment_currency: string;
  commitment_date?: string;
  status: 'ACTIVE' | 'PENDING' | 'EXITED' | 'DEFAULTED';
  subscription_agreement_executed?: boolean;

  has_custom_terms?: boolean;
  custom_investment_period_fee_rate?: number;
  custom_investment_period_fee_basis_id?: number;
  custom_post_investment_period_fee_rate?: number;
  custom_post_investment_period_fee_basis_id?: number;
  custom_investment_period_duration?: number;
  custom_gp_commitment?: number;
  custom_hurdle_rate?: number;
  custom_carried_interest?: number;
  custom_catch_up_rate?: number;
  custom_distribution_waterfall_type_id?: number;

  items: InvestorFundRelationshipBulkItem[];
}

export interface InvestorFundRelationshipBulkCreateResponse {
  created_count: number;
  created_ids: string[];
}

// Filter and search types
export interface InvestorFilters {
  investor_type?:
    | 'corporate'
    | 'family_office'
    | 'individual'
    | 'fund_of_funds'
    | 'institutional';
  kyc_status?: 'pending' | 'verified' | 'rejected';
  search?: string;
  fund?: string;
  has_pending_changes?: boolean;
  include_archived?: boolean;
}

export interface InvestorDeleteResponse {
  action: 'deleted' | 'archived';
  message: string;
}

export interface FundRelationshipDeleteResponse {
  action: 'deleted' | 'exited';
  message: string;
}

// Type aliases
export type InvestorType =
  | 'corporate'
  | 'family_office'
  | 'individual'
  | 'fund_of_funds'
  | 'institutional';
export type KycStatus = 'pending' | 'verified' | 'rejected';
export type AmlStatus = 'pending' | 'verified' | 'rejected';

// Constants
export const INVESTOR_TYPES = {
  corporate: 'Corporate',
  family_office: 'Family Office',
  individual: 'Individual',
  fund_of_funds: 'Fund of Funds',
  institutional: 'Institutional',
} as const;

export const KYC_STATUS_OPTIONS = {
  pending: 'Pending',
  verified: 'Verified',
  rejected: 'Rejected',
} as const;

// AML status uses the same options as KYC status (same backend validation)
export const AML_STATUS_OPTIONS = KYC_STATUS_OPTIONS;

export interface RawUnconvertedCLAItem {
  portfolio_company_name: string;
  instrument_type: string;
  effective_value: string;
}

export interface RawInvestorInvestmentFundCommitment {
  id: string;
  name: string;
  commitment: string;
  drawn: string;
  undrawn: string;
  invested: string;
  nav: string;
  fees: string;
  status: string;
  draw_percentage: string;
  unconverted_cla_exposure: string;
  unconverted_clas: RawUnconvertedCLAItem[];
}

export interface RawInvestorInvestmentOverview {
  total_commitment: string;
  drawn_capital: string;
  undrawn_capital: string;
  total_invested: string;
  nav: string;
  total_fees: string;
  total_unconverted_cla_exposure: string;
  fund_count: number;
  currency: string;
  funds: RawInvestorInvestmentFundCommitment[];
}

// Domain types: monetary fields are number, post-mapper.

export interface UnconvertedCLAItem {
  portfolio_company_name: string;
  instrument_type: string;
  effective_value: number;
}

export interface InvestorInvestmentFundCommitment {
  id: string;
  name: string;
  commitment: number;
  drawn: number;
  undrawn: number;
  invested: number;
  nav: number;
  fees: number;
  status: string;
  draw_percentage: number;
  unconverted_cla_exposure: number;
  unconverted_clas: UnconvertedCLAItem[];
}

export interface InvestorInvestmentOverview {
  total_commitment: number;
  drawn_capital: number;
  undrawn_capital: number;
  total_invested: number;
  nav: number;
  total_fees: number;
  total_unconverted_cla_exposure: number;
  fund_count: number;
  currency: string;
  funds: InvestorInvestmentFundCommitment[];
}

export type InvestorTransferDirection = 'TRANSFERRED_IN' | 'TRANSFERRED_OUT';

export interface RawInvestorTransfer {
  fund_transaction_id: string;
  fund_id: string;
  fund_name: string;
  currency: string;
  effective_date: string;
  direction: InvestorTransferDirection;
  counterparty_investor_id: string;
  counterparty_name: string;
  commitment_amount: string;
  status: FundTransactionStatus;
}

export interface InvestorTransfer extends Omit<
  RawInvestorTransfer,
  'commitment_amount'
> {
  commitment_amount: number;
}

export type LpTransferRecalculatedKind =
  'drawdown' | 'distribution' | 'investment';

export interface LpTransferRecalculatedDraft {
  kind: LpTransferRecalculatedKind;
  id: string;
  label: string;
}

export interface LpTransferImpact {
  recalculated: LpTransferRecalculatedDraft[];
  blockers: string[];
}

export interface LpTransferDraftChange {
  effective_date: string;
  transferee_investor_id: string;
  transfer_commitment_amount: string;
}

export interface LpTransferDraftUpdate extends LpTransferDraftChange {
  transaction_name: string | null;
}

export interface LpTransferUpdateResponse {
  id: string;
}
